
import React from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Star, MessageSquare, Plus, Check, EyeOff, MapPin, GraduationCap, Heart } from "lucide-react";
import { Profile } from "@/hooks/dashboard/useUserProfilesData";
import { User as SupabaseUser } from "@supabase/supabase-js";
import { motion } from "framer-motion";

interface UserProfileCardProps {
  profile: Profile;
  index: number;
  isFollowing: boolean;
  currentUser: SupabaseUser | null;
  onFollow: (userId: string) => void;
  onMessage: (userId: string, username: string | null) => void;
  onViewProfile: (userId: string) => void;
}

const getInitials = (name: string | null) => {
  return name?.split(' ')
    .map(part => part[0])
    .join('')
    .toUpperCase() || 'U';
};

const UserProfileCard: React.FC<UserProfileCardProps> = ({ 
  profile, 
  index, 
  isFollowing, 
  currentUser, 
  onFollow, 
  onMessage,
  onViewProfile 
}) => {
  return (
    <motion.div 
      className="bg-background/50 backdrop-blur-sm p-4 rounded-xl border border-border/30 hover:border-border/60 transition-all duration-300 hover:shadow-md"
      whileHover={{ y: -5, boxShadow: "0 10px 25px -5px rgba(0,0,0,0.1)" }}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: index * 0.1 }}
    >
      <div className="flex items-start gap-4">
        <div className="relative flex-shrink-0">
          <Avatar className="h-16 w-16 ring-2 ring-primary/20 cursor-pointer" onClick={() => onViewProfile(profile.id)}>
            <AvatarImage src={profile.avatar_url || undefined} />
            <AvatarFallback className="bg-gradient-to-br from-primary/20 to-secondary/20 text-lg font-semibold">
              {getInitials(profile.full_name)}
            </AvatarFallback>
          </Avatar>
          <motion.div 
            className="absolute -top-2 -right-2 bg-gradient-to-r from-primary to-primary/80 text-primary-foreground rounded-full h-7 w-7 flex items-center justify-center text-xs font-bold shadow-sm"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            {index + 1}
          </motion.div>
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between mb-2">
            <div className="flex-1 min-w-0">
              <motion.button 
                onClick={() => onViewProfile(profile.id)}
                className="font-semibold text-foreground hover:text-primary cursor-pointer transition-colors text-left truncate block w-full"
                whileHover={{ scale: 1.01 }}
              >
                {profile.full_name || 'Anonymous User'}
              </motion.button>
              <p className="text-sm text-muted-foreground">@{profile.username || 'username'}</p>
            </div>
            
            {(profile.karma_points || 0) >= 100 && (
              <Badge variant="secondary" className="ml-2 flex items-center gap-1 bg-gradient-to-r from-amber-500/10 to-orange-500/10 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800">
                <Star className="h-3 w-3 text-amber-500 fill-amber-500" />
                {profile.karma_points}
              </Badge>
            )}
          </div>

          {profile.bio && (
            <p className="text-sm text-muted-foreground line-clamp-2 mb-3 leading-relaxed">{profile.bio}</p>
          )}

          <div className="flex items-center gap-3 text-xs text-muted-foreground mb-3">
            {profile.grade_level && (
              <div className="flex items-center gap-1 bg-muted/50 px-2 py-0.5 rounded-full">
                <GraduationCap className="h-3 w-3" />
                <span>{profile.grade_level}</span>
              </div>
            )}
            {profile.country && (
              <div className="flex items-center gap-1 bg-muted/50 px-2 py-0.5 rounded-full">
                <MapPin className="h-3 w-3" />
                <span>{profile.state ? `${profile.state}` : profile.country}</span>
              </div>
            )}
          </div>

          <div className="flex gap-2">
            <Button 
              variant={isFollowing ? "secondary" : "default"} 
              size="sm" 
              className={`flex-1 h-8 text-xs font-medium transition-all duration-200 ${
                isFollowing 
                  ? "bg-green-100 hover:bg-green-200 text-green-700 dark:bg-green-950 dark:text-green-400 dark:hover:bg-green-900" 
                  : "bg-primary hover:bg-primary/90 text-primary-foreground shadow-sm hover:shadow-md"
              }`}
              onClick={() => onFollow(profile.id)}
              disabled={!currentUser}
              title={!currentUser ? "Log in to follow" : (isFollowing ? "Unfollow" : "Follow")}
            >
              {!currentUser ? (
                <>
                  <EyeOff className="h-3 w-3 mr-1" /> Follow
                </>
              ) : isFollowing ? (
                <>
                  <Check className="h-3 w-3 mr-1" /> Following
                </>
              ) : (
                <>
                  <Plus className="h-3 w-3 mr-1" /> Follow
                </>
              )}
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              className="flex-1 h-8 text-xs font-medium hover:bg-muted/50 transition-all duration-200"
              onClick={() => onMessage(profile.id, profile.username)}
              disabled={!currentUser}
              title={!currentUser ? "Log in to message" : "Send message"}
            >
              {!currentUser ? (
                <>
                  <EyeOff className="h-3 w-3 mr-1" /> Message
                </>
              ) : (
                <>
                  <MessageSquare className="h-3 w-3 mr-1" /> Message
                </>
              )}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 aspect-square px-0 text-xs font-medium transition-all duration-200 text-rose-500 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950"
              onClick={() => onViewProfile(profile.id)}
              title="View profile"
            >
              <Heart className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default UserProfileCard;
